const BASE_HEIGHT = 288;

export interface ExpiryTideBands {
  premium: { top: number; height: number };
  spot: { top: number; height: number };
  volume: { top: number; height: number };
}

interface MarketDatedBar {
  market_date: string;
}

/** Preserve ORDER 27's vertical composition at whatever height CSS gives the SVG. */
export function expiryTideBands(renderedHeight: number): ExpiryTideBands {
  if (!Number.isFinite(renderedHeight) || renderedHeight < 0) {
    throw new RangeError("expiry tide rendered height must be finite and non-negative");
  }
  const y = (baseline: number) => baseline * renderedHeight / BASE_HEIGHT;
  return {
    premium: { top: y(17), height: y(126) },
    spot: { top: y(160), height: y(50) },
    volume: { top: y(226), height: y(42) },
  };
}

/** Compact alert ink. Hours remain hours so a multi-session stale read stays unmistakable. */
export function staleAgeChip(stale: boolean, ageS: number | null | undefined): string | null {
  if (!stale) return null;
  // The adjacent AgePill owns this door's one age. This neighbour keeps only its verdict.
  void ageS;
  return "STALE";
}

function sessionDate(bars: readonly MarketDatedBar[]): string | null {
  if (bars.length === 0) return null;
  const value = bars.at(-1)!.market_date;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new TypeError(`expiry tide market_date is invalid: ${value}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new TypeError(`expiry tide market_date is invalid: ${value}`);
  }
  return value;
}

/** The visible session is deliberately taken from the last plotted bar. */
export function sessionPill(bars: readonly MarketDatedBar[]): string | null {
  const value = sessionDate(bars);
  if (value === null) return null;
  const [year, month, day] = value.split("-").map(Number);
  const weekday = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"]
    [new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  return `${weekday} ${day}`;
}

/** Expanded receipt names the source date and declares a mixed-date last-bar choice. */
export function sessionInfo(bars: readonly MarketDatedBar[]): string | null {
  const value = sessionDate(bars);
  if (value === null) return null;
  const pill = sessionPill(bars)!;
  const mixed = bars.some((bar) => bar.market_date !== value);
  return `SESSION ${pill} · market_date ${value}${mixed ? " · MIXED MARKET DATES · LAST BAR SHOWN" : ""}`;
}
